import { useContext } from 'react';
import { SearchAdvancedContext } from './SearchAdvancedContextProvider';
import {
  LocalAdvancedSearchContext,
  LocalAdvancedSearchContextType,
} from './LocalAdvancedSearchProvider';
import { SearchAdvancedContextType } from '../../types';

/**
 *
 * Hook to use either LocalAdvanced or SearchAdvanced context.
 *
 * Because this is determined at runtime which context is available,
 * we use a generic T type to allow callers to specify which context type
 * they expect to receive.
 *
 * T type defaults to SearchAdvancedContextType so consumers can
 * call useAdvancedSearchContext() and get the "main" shape by default.
 *
 * eg: In main editorial search (default case)
 * const { showFilters, handleQpValueUpdate } = useAdvancedSearchContext();
 * // showFilters, etc will have correct types
 *
 * If you are certain we are in a modal-only context you can
 * explicitly set T to LocalAdvancedSearchContextType:
 *
 * const {...} = useAdvancedSearchContext<LocalAdvancedSearchContextType>();
 * // Returned types will have LocalAdvancedSearchContextType
 *
 */

/**
 * One of these two context types will be returned
 */
type AdvancedSearchContextResult =
  | SearchAdvancedContextType
  | LocalAdvancedSearchContextType;

export const useAdvancedSearchContext = <
  T extends AdvancedSearchContextResult = SearchAdvancedContextType,
>(): T => {
  // Main search context (URL-based, full-featured for editorial search screen)
  const mainSearchContext = useContext(SearchAdvancedContext);

  // Modal search context (local-state, streamlined for modals and asset library)
  const modalSearchContext = useContext(LocalAdvancedSearchContext);

  // Important: Prioritize a parent 'local' modal context if found first in react tree
  // This will allow us to mount a modal within a "mainSearchContext" given that its
  // first parent ctx is a wrapper of <LocalAdvancedSearchProvider>.
  if (modalSearchContext) {
    return modalSearchContext as T;
  }

  // Default: use main search context
  // treat as SearchAdvancedContextType for T
  if (mainSearchContext) {
    return mainSearchContext as T;
  }

  // Neither context is available
  throw new Error(
    'useAdvancedSearchContext must be used within either a SearchAdvancedProvider or LocalAdvancedSearchProvider',
  );
};
